// SF5 — Promotion Conflict Detector.
//
// NOTHING HERE IS SUBMITTED TO AMAZON. The seller sketches what they intend to
// run and gets back what will go wrong with it. That is deliberately a local
// scratchpad rather than a read of scheduled promotions: there is no scheduled-
// promotion source yet, and a planner that only works once one exists is a
// planner nobody can use today.
//
// SEVERITY IS THE OUTPUT, not a decoration. "Amazon will reject this" and "this
// will be measured against demand another promotion already pulled forward"
// both matter and need completely different reactions, so they are never shown
// as one undifferentiated list of warnings.
import React, { useContext, useState } from 'react';
import { FiAlertTriangle, FiInfo, FiPlus, FiTrash2 } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../PromoCalendar.module.css';
import { postConflicts } from '../services/calendarApi';

const KINDS = [
  { key: 'deal', label: 'Deal' },
  { key: 'coupon', label: 'Coupon' },
  { key: 'price_discount', label: 'Price discount' },
];

const SEVERITY_CLASS = {
  blocking: 'severityBlocking',
  warning: 'severityWarning',
  informational: 'severityInformational',
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const plusDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const blankRow = () => ({
  sku: '', name: '', kind: 'deal',
  start: plusDays(14), end: plusDays(18),
  stock_cover_days: '', buy_box_held: true,
});

const ConflictChecker = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [rows, setRows] = useState([blankRow()]);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const update = (index, field, value) => {
    setRows((current) => current.map((row, position) =>
      (position === index ? { ...row, [field]: value } : row)));
  };

  const check = async () => {
    if (!token) return;
    setChecking(true);
    setError(null);
    try {
      const scheduled = rows
        .filter((row) => row.sku && row.start && row.end)
        .map((row) => ({
          sku: row.sku, kind: row.kind,
          name: row.name || `${row.kind} on ${row.sku}`,
          start: row.start, end: row.end,
        }));
      // Stock cover and Buy Box are per SKU, not per promotion — the same
      // product cannot have two different stock positions on one day.
      const stockCover = {};
      const buyBox = {};
      rows.forEach((row) => {
        if (!row.sku) return;
        if (row.stock_cover_days !== '') {
          stockCover[row.sku] = Number(row.stock_cover_days);
        }
        buyBox[row.sku] = row.buy_box_held;
      });
      setResult(await postConflicts(token, {
        scheduled, stock_cover_days: stockCover, buy_box_held: buyBox,
      }));
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setChecking(false);
    }
  };

  const conflicts = result?.conflicts || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What will go wrong with what you plan</h2>
        {result && (
          <span className={local.muted}>
            {result.counts.blocking} blocking · {result.counts.warning} warnings
          </span>
        )}
      </div>
      <p className={local.muted}>
        Sketch what you intend to run. Nothing here is submitted, created or
        edited on Amazon — it only tells you which of these Amazon will reject,
        which will run empty, and which will be measured against demand another
        promotion already pulled forward.
      </p>

      <div className={local.tableScroll}>
        <table className={local.table}>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Type</th>
              <th>From</th>
              <th>To</th>
              <th className={local.numeric}>Stock cover (days)</th>
              <th>Buy Box held</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                <td>
                  <input value={row.sku} placeholder="SKU"
                    onChange={(e) => update(index, 'sku', e.target.value)} />
                </td>
                <td>
                  <select value={row.kind}
                    onChange={(e) => update(index, 'kind', e.target.value)}>
                    {KINDS.map((kind) => (
                      <option key={kind.key} value={kind.key}>{kind.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input type="date" value={row.start} min={todayIso()}
                    onChange={(e) => update(index, 'start', e.target.value)} />
                </td>
                <td>
                  <input type="date" value={row.end} min={row.start}
                    onChange={(e) => update(index, 'end', e.target.value)} />
                </td>
                <td className={local.numeric}>
                  <input type="number" min={0} value={row.stock_cover_days}
                    placeholder="—"
                    onChange={(e) => update(index, 'stock_cover_days', e.target.value)} />
                </td>
                <td>
                  <input type="checkbox" checked={row.buy_box_held}
                    onChange={(e) => update(index, 'buy_box_held', e.target.checked)} />
                </td>
                <td>
                  {rows.length > 1 && (
                    <button type="button" className={local.crumb}
                      onClick={() => setRows((current) =>
                        current.filter((_, position) => position !== index))}>
                      <FiTrash2 />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={local.controls} style={{ marginTop: 12 }}>
        <button type="button" className={local.crumb}
          onClick={() => setRows((current) => [...current, blankRow()])}>
          <FiPlus /> Add another
        </button>
        <button type="button" className={local.primary} onClick={check}
          disabled={checking}>
          {checking ? 'Checking…' : 'Check for conflicts'}
        </button>
      </div>

      {error && (
        <div className={`${local.banner} ${local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div><strong>Could not check</strong><div>{error.message}</div></div>
        </div>
      )}

      {result && conflicts.length === 0 && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>Nothing found against {result.scheduled} scheduled</strong>
            <div>
              No overlaps, no reference-price clashes, and stock covers what you
              entered. This checks what you typed — it does not know about
              promotions you have not listed here.
            </div>
          </div>
        </div>
      )}

      {conflicts.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {conflicts.map((conflict, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className={local.conflictRow}>
              <span className={local[SEVERITY_CLASS[conflict.severity]]}>
                {conflict.severity}
              </span>
              <span>
                <strong>{conflict.sku}</strong>
                {conflict.between?.length > 1 && ` · ${conflict.between.join(' + ')}`}
                <div className={local.muted}>{conflict.message}</div>
              </span>
            </div>
          ))}
        </div>
      )}

      {result?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {result.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ConflictChecker;
